import { useEffect } from 'react';
import { isEmpty } from 'lodash-es';
import { InfiniteData, useMutation } from 'react-query';
import { useTranslation } from '@frontend/i18n';
import { downloadCSV } from '@frontend/media-helpers';
import { useMultiQueryKey, useMultiLocationUtils } from '@frontend/payments-hooks';
import { useAppScopeStore, useScopedInfiniteQuery } from '@frontend/scope';
import { formatDate, useAlert } from '@frontend/design-system';
import { formatCentsToCurrency } from '../utils/currency-helpers';
import { makePersonName } from '../utils/failed-transactions-helpers';
import { getPaymentMethodBrand, GetLocationName } from '../utils/helpers';
import { usePaginatedFailedInvoicesQueryData } from './payment-failed/hooks';
import { getFailedPayments } from './payment-failed/query';
import {
  SearchFailedPaymentsModel,
  SearchFailedPaymentsParams,
  SearchFailedPaymentsResponse,
} from './payment-failed/types';
import {
  calculateFailedTransactionsSummary,
  getFailedTransactionsOriginLabel,
  getFailedTransactionsQueryKey,
  mapFailedPaymentsTypeToPaymentType,
} from './payment-failed/utils';

type UseQueryPaginatedFailedInvoicesProps<T> = {
  currentPage: number;
  onChangePage: (currentPage: number) => void;
  onResetPage: () => void;
  searchParams: SearchFailedPaymentsParams;
  select?: (data: InfiniteData<SearchFailedPaymentsResponse>) => InfiniteData<SearchFailedPaymentsResponse & T>;
};
export const useQueryPaginatedFailedInvoices = <T>({
  currentPage,
  searchParams,
  onChangePage,
  onResetPage,
  select,
}: UseQueryPaginatedFailedInvoicesProps<T>) => {
  const { selectedLocationIds: locationIds } = useAppScopeStore();

  useEffect(() => {
    onResetPage();
  }, [locationIds]);

  const queryKey = getFailedTransactionsQueryKey(searchParams);

  const infiniteQueryProps = useScopedInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }: { pageParam?: string | null }) => {
      return getFailedPayments({
        locationIds,
        params: { ...searchParams, after: pageParam ?? undefined },
      });
    },
    getNextPageParam: (lastPage) => lastPage.lastEdgeCursor,

    select,
    staleTime: 60 * 1000,
    cacheTime: 2 * 60 * 1000,
    retry: 3,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  const loading = infiniteQueryProps.isLoading || infiniteQueryProps.isFetching;
  return {
    infiniteQueryProps,
    loading,
    isFetchingNextPage: infiniteQueryProps.isFetchingNextPage,
    // renaming to transactions breaks the convention of react query
    // pulling off specific properties should be done in a `select` method, not in the reusable query hook
    transactions:
      !loading && infiniteQueryProps.data?.pages ? infiniteQueryProps.data.pages[currentPage - 1]?.payments ?? [] : [], // The backend returns a `null` if there are no items, instead of `[]`
    hasMore:
      !loading && infiniteQueryProps.data?.pages
        ? infiniteQueryProps.data.pages[currentPage - 1]?.meta?.hasNextPage
        : false,
    hasPrevious:
      !loading && infiniteQueryProps.data?.pages
        ? infiniteQueryProps.data.pages[currentPage - 1]?.meta?.hasPreviousPage
        : false,
    numRows:
      searchParams.first ||
      (!loading && infiniteQueryProps.data?.pages
        ? infiniteQueryProps.data.pages[currentPage - 1]?.totalCount || 0
        : 0),
    currentPage,
    refetch: () => infiniteQueryProps.refetch(), // Just to keep API consistent with saga hook
    fetchNextPage: () => {
      if (infiniteQueryProps.data?.pages && infiniteQueryProps.data?.pages.length === currentPage) {
        infiniteQueryProps.fetchNextPage();
      }
      onChangePage(currentPage + 1);
    },
    fetchPreviousPage: () => {
      onChangePage(Math.max(1, currentPage - 1));
    },
  };
};

export type UseQueryAllFailedInvoicesProps = {
  searchParams: SearchFailedPaymentsParams;
  currentPage?: number;
  numRows?: number;
  enable?: boolean;
};
export const useQueryAllFailedInvoices = ({
  searchParams,
  numRows = 10,
  currentPage = 1,
  enable = true,
}: UseQueryAllFailedInvoicesProps) => {
  const { selectedLocationIds: locationIds } = useAppScopeStore();
  const { getMultiQueryKey } = useMultiQueryKey(locationIds);
  const queryKey = getMultiQueryKey(['all-failed-invoices', locationIds, searchParams]);

  const hasDateRangeFilters =
    !!searchParams?.submittedAt?.greaterThanEqual || !!searchParams?.submittedAt?.lessThanEqual;
  const NUM_ROWS = hasDateRangeFilters ? 500 : numRows;

  const { previousPageEndCursor } = usePaginatedFailedInvoicesQueryData({
    searchParams,
    currentPage,
  });

  const { fetchNextPage, hasNextPage, data, isFetchingNextPage, isLoading, isFetching } = useScopedInfiniteQuery({
    enabled: enable,
    queryKey,
    queryFn: ({ pageParam }: { pageParam?: string | null }) =>
      getFailedPayments({
        locationIds,
        params: {
          ...searchParams,
          first: NUM_ROWS,
          after: pageParam ?? (!hasDateRangeFilters ? previousPageEndCursor : undefined),
        },
      }),
    getNextPageParam: (currentPage) => {
      return currentPage.lastEdgeCursor;
    },
  });
  if (hasNextPage && !isFetchingNextPage && hasDateRangeFilters) {
    fetchNextPage();
  }

  const loading = isLoading || isFetching || isFetchingNextPage;

  const transactions =
    (hasDateRangeFilters
      ? data?.pages?.flatMap((page) => page.payments ?? []).filter((x): x is SearchFailedPaymentsModel => !!x)
      : data?.pages?.[0]?.payments) ?? [];

  const summary = isEmpty(searchParams)
    ? transactions?.length
      ? calculateFailedTransactionsSummary(transactions)
      : undefined
    : calculateFailedTransactionsSummary(data?.pages?.[currentPage - 1]?.payments || []);

  return {
    loading,
    transactions,
    summary,
  };
};

export type UseExportFailedTransactionsQueryProps = {
  searchParams: SearchFailedPaymentsParams;
  numRows?: number;
  currentPage?: number;
};
export const useExportFailedTransactionsQuery = ({
  searchParams,
  numRows = 10,
  currentPage,
}: UseExportFailedTransactionsQueryProps) => {
  const { selectedLocationIds: locationIds } = useAppScopeStore();

  const { getLocationName } = useMultiLocationUtils();
  const { getMultiQueryKey } = useMultiQueryKey(locationIds);
  const alerts = useAlert();
  const { t } = useTranslation('payments');

  const NUM_ROWS =
    !!searchParams?.submittedAt?.lessThanEqual || !!searchParams?.submittedAt?.greaterThanEqual ? 500 : numRows;

  const { previousPageEndCursor } = usePaginatedFailedInvoicesQueryData({
    searchParams,
    currentPage,
  });

  const {
    data: transactions,
    mutate: exportFailedTransactions,
    isLoading: isExporting,
  } = useMutation<SearchFailedPaymentsResponse>({
    mutationKey: getMultiQueryKey(['failed-transactions', locationIds, searchParams]),
    mutationFn: () => {
      return getFailedPayments({
        locationIds,
        params: { ...searchParams, first: NUM_ROWS, after: previousPageEndCursor },
      });
    },
    onSuccess: ({ payments }) => {
      const transformedData = transformDataForCsvExport(payments, getLocationName);
      alerts.success(t('We’re working on exporting your data. It will download shortly.'));
      downloadCSV(transformedData, 'failed_transactions_list');
    },
    onError: (err) => {
      alerts.error(t('Failed to export failed transaction history'));
      console.error(err);
    },
  });

  return { transactions, isExporting, exportFailedTransactions };
};

const transformDataForCsvExport = (data: SearchFailedPaymentsModel[] = [], getLocationName: GetLocationName) => {
  return data.map((row) => {
    const amount = formatCentsToCurrency(row.amount)?.replace(',', '') || 'Not Available';
    const dateTime = formatDate(row.submittedAt, 'YYYY-MM-D');
    const locationName = getLocationName(row.locationId ?? '') ?? '';
    const personName = makePersonName(row.personFirstName, row.personLastName);
    const origin = getFailedTransactionsOriginLabel(row.origin);

    const paymentMethod = getPaymentMethodBrand({
      paymentType: mapFailedPaymentsTypeToPaymentType(row.paymentType),
      brand: row.cardBrand,
      bankName: row.bankName,
    });

    const fullPaymentMethod = makePersonName(paymentMethod, row.cardLastFour);

    return {
      'Transaction ID:': row.invoiceId,
      'Location Name': locationName,
      'Patient Id': row.personId,
      'Customer Name': personName,
      Amount: amount,
      'Date / Time': dateTime,
      'Payment Origin': origin,
      'Card Type': fullPaymentMethod,
      Status: row.paymentStatus,
      'Reason For Failure': `"${row.paymentStatusReason.replace(/"/g, '""')}"`,
      'Office User': row.userName,
    };
  });
};
